import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createBXPCallingList,
  updateBXPCallingList,
  deleteBXPCallingList,
  BXPCallingList
} from 'services/hvaservicecampaign.service';

interface CallingListsTableProps {
  callingLists: BXPCallingList[];
  onCallingListsChange: () => Promise<void>;
}

const CallingListsChangeContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

const useCallingListsChange = () => {
  const context = useContext(CallingListsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useCallingListsChange must be used within a CallingListsChangeProvider'
    );
  }
  return context;
};

type CallingListActionCellProps = {
  callingList: BXPCallingList;
};

const CallingListActionCell = ({ callingList }: CallingListActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line
  const [selectedCallingList, setSelectedCallingList] = useState<BXPCallingList | null>(null);
  const onCallingListsChange = useCallingListsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCallingList(null);
  };

  const handleShowEditModal = () => {
    setSelectedCallingList(callingList);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteBXPCallingList(callingList.id);
      console.log(
        `Calling List with ID: ${callingList.id} deleted successfully`
      );
      await onCallingListsChange();
    } catch (error) {
      console.error(
        `Error deleting Calling List with ID: ${callingList.id}`,
        error
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedCallingList) {
      try {
        await updateBXPCallingList(selectedCallingList.id, selectedCallingList);
        await onCallingListsChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating calling list: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }
    // eslint-disable-next-line
    setSelectedCallingList(prev => (prev ? { ...prev, [name]: newValue } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Calling List?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Calling List</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={selectedCallingList?.shortname || ''}
                onChange={handleInputChange}
                required
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Table Name</Form.Label>
              <Form.Control
                type="text"
                name="tablename"
                value={selectedCallingList?.tablename || ''}
                onChange={handleInputChange}
                required
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={selectedCallingList?.active || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Calling List
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const callingListsTableColumns: ColumnDef<BXPCallingList>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'shortname',
    header: 'Short Name',
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <CallingListActionCell callingList={original} />;
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'tablename',
    header: 'Table Name',
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '60%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  }
];

export const BXPCallingListsTable: React.FC<CallingListsTableProps> = ({
  callingLists,
  onCallingListsChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCallingList, setNewCallingList] = useState<BXPCallingList>({
    id: 0,
    shortname: '',
    tablename: '',
    active: true,
    crr_username: '',
    crr_time: new Date()
  });
  const table = useAdvanceTable({
    data: callingLists,
    columns: callingListsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setNewCallingList(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createBXPCallingList(newCallingList);
      handleCloseAddModal();
      onCallingListsChange();
    } catch (error) {
      console.error('Error creating calling list:', error);
    }
  };

  return (
    <CallingListsChangeContext.Provider value={onCallingListsChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Calling Lists"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Calling List
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Calling List</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={newCallingList.shortname}
                onChange={handleInputChange}
                required
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Table Name</Form.Label>
              <Form.Control
                type="text"
                name="tablename"
                value={newCallingList.tablename}
                onChange={handleInputChange}
                required
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={newCallingList.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Calling List
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </CallingListsChangeContext.Provider>
  );
};
